<script setup>
import { ref, computed, onMounted } from 'vue';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';
import ShortLinksAPI from 'dashboard/api/shortLinks';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const links = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const editingCode = ref(null); // shortCode being edited, null = create mode

const emptyForm = () => ({
  longUrl: '',
  title: '',
  customSlug: '',
  neverExpires: true,
  expiresAt: '', // datetime-local value when neverExpires is false
});

const form = ref(emptyForm());

const isEditing = computed(() => Boolean(editingCode.value));

const pad = n => String(n).padStart(2, '0');
const toLocalInput = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const isUrlValid = computed(() => /^https?:\/\/.+/i.test(form.value.longUrl.trim()));
// When an expiry is required, it must be set and in the future.
const isExpiryValid = computed(
  () =>
    form.value.neverExpires ||
    (Boolean(form.value.expiresAt) &&
      new Date(form.value.expiresAt).getTime() > Date.now())
);
const canSubmit = computed(() => isUrlValid.value && isExpiryValid.value);

const validUntilOf = link => link.meta?.validUntil || null;

const visitsOf = link =>
  link.visitsSummary?.total ?? link.visitsCount ?? 0;

// Per-link visit detail: which link is expanded + lazily-loaded visit rows.
const expandedCode = ref(null);
const visitsByCode = ref({});

// "City, Country" from Shlink's visitLocation, or null when geo is unresolved.
const locationOf = visit => {
  const loc = visit.visitLocation;
  if (!loc) return null;
  return [loc.cityName, loc.countryName].filter(Boolean).join(', ') || null;
};

// Referrer domain only (e.g. instagram.com); null when there is no referrer.
const refererDomain = visit => {
  if (!visit.referer) return null;
  try {
    return new URL(visit.referer).hostname.replace(/^www\./, '');
  } catch {
    return visit.referer;
  }
};

// Non-browser / scanner user agents that Shlink's own potentialBot flag misses
// (e.g. TLM-Audit-Scanner, curl, python-requests). Empty UA is treated the same.
const SCANNER_UA =
  /(scanner|curl|wget|python-requests|python-urllib|aiohttp|go-http-client|libwww|httpie|headless|\bLinks \()/i;

// 'bot' | 'preview' | 'human' — the *quality* of the visit (the "where from" lives
// in the Referente column):
// - bot: Shlink flagged it, or a scanner/empty UA we caught ourselves.
// - preview: Meta (Facebook/Instagram) prefetches the link to build its preview
//   card — Meta referer but a generic desktop UA (no in-app token).
const visitType = visit => {
  const ua = visit.userAgent || '';
  if (visit.potentialBot || !ua || SCANNER_UA.test(ua)) return 'bot';
  const isMetaReferer = /^(facebook|instagram|fb)\.com$/i.test(
    refererDomain(visit) || ''
  );
  const isInAppClick = /instagram|fban|fbav|fb_iab/i.test(ua);
  if (isMetaReferer && !isInAppClick) return 'preview';
  return 'human';
};

// Source label for the Referente column: a domain when there is one, or
// "Directo / App" for a no-referrer mobile tap (app/QR/direct). Hidden for bots.
const refererLabel = visit => {
  if (visitType(visit) === 'bot') return null;
  const domain = refererDomain(visit);
  if (domain) return domain;
  if (/android|iphone|ipad|mobile/i.test(visit.userAgent || '')) {
    return 'Directo / App';
  }
  return null;
};

// Human-readable browser · OS from the raw user agent (good enough for a label).
const parseUserAgent = ua => {
  if (!ua) return 'Desconocido';
  const browser = /Edg/i.test(ua)
    ? 'Edge'
    : /OPR|Opera/i.test(ua)
      ? 'Opera'
      : /Chrome/i.test(ua)
        ? 'Chrome'
        : /Firefox/i.test(ua)
          ? 'Firefox'
          : /Safari/i.test(ua)
            ? 'Safari'
            : null;
  const os = /Windows/i.test(ua)
    ? 'Windows'
    : /Android/i.test(ua)
      ? 'Android'
      : /iPhone|iPad|iOS/i.test(ua)
        ? 'iOS'
        : /Mac OS X|Macintosh/i.test(ua)
          ? 'macOS'
          : /Linux/i.test(ua)
            ? 'Linux'
            : null;
  return [browser, os].filter(Boolean).join(' · ') || 'Otro';
};

const loadVisits = async code => {
  visitsByCode.value[code] = { loading: true, rows: [], error: null };
  try {
    const { data } = await ShortLinksAPI.visits(code);
    visitsByCode.value[code] = {
      loading: false,
      rows: data.payload?.data || [],
      error: null,
    };
  } catch (error) {
    visitsByCode.value[code] = {
      loading: false,
      rows: [],
      error:
        error?.response?.data?.error || 'No se pudieron cargar las visitas.',
    };
  }
};

// Toggle the visit panel; load visits once on first expand (single-open accordion).
const toggleVisits = async link => {
  const code = link.shortCode;
  if (expandedCode.value === code) {
    expandedCode.value = null;
    return;
  }
  expandedCode.value = code;
  if (!visitsByCode.value[code]) await loadVisits(code);
};

// Refresh a single link in place (updated visit count, title, expiry). Reloads
// its visit panel too when expanded.
const refreshingCode = ref(null);
const refreshLink = async link => {
  const code = link.shortCode;
  refreshingCode.value = code;
  try {
    const { data } = await ShortLinksAPI.show(code);
    links.value = links.value.map(l => (l.shortCode === code ? data.payload : l));
    if (expandedCode.value === code) await loadVisits(code);
  } catch (error) {
    useAlert(error?.response?.data?.error || 'No se pudo actualizar el enlace.');
  } finally {
    refreshingCode.value = null;
  }
};

// QR code: which link's QR panel is open + the generated PNG data URL.
const qrCode = ref(null); // shortCode whose QR panel is open
const qrDataUrl = ref('');
const qrGenerating = ref(false);

const generateQr = async shortUrl => {
  qrGenerating.value = true;
  try {
    qrDataUrl.value = await QRCode.toDataURL(shortUrl, { width: 512, margin: 1 });
  } catch {
    qrDataUrl.value = '';
    useAlert('No se pudo generar el código QR.');
  } finally {
    qrGenerating.value = false;
  }
};

const toggleQr = async link => {
  const code = link.shortCode;
  if (qrCode.value === code) {
    qrCode.value = null;
    qrDataUrl.value = '';
    return;
  }
  qrCode.value = code;
  await generateQr(link.shortUrl);
};

const refreshQr = link => generateQr(link.shortUrl);

const downloadQr = link => {
  if (!qrDataUrl.value) return;
  const a = document.createElement('a');
  a.href = qrDataUrl.value;
  a.download = `${link.shortCode}.png`;
  a.click();
};

const copyQr = async () => {
  if (!qrDataUrl.value) return;
  try {
    const blob = await (await fetch(qrDataUrl.value)).blob();
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob }),
    ]);
    useAlert('Código QR copiado');
  } catch {
    useAlert('No se pudo copiar el código QR.');
  }
};

const formatDate = value => {
  if (!value) return '';
  return new Date(value).toLocaleString('es', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fetchLinks = async () => {
  isLoading.value = true;
  try {
    const { data } = await ShortLinksAPI.get();
    links.value = data.payload || [];
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'No se pudieron cargar los enlaces.'
    );
  } finally {
    isLoading.value = false;
  }
};

// ISO 8601 expiry when set, null when "never expires". null clears it on edit.
const expiryPayload = () =>
  !form.value.neverExpires && form.value.expiresAt
    ? new Date(form.value.expiresAt).toISOString()
    : null;

const cancelEdit = () => {
  editingCode.value = null;
  form.value = emptyForm();
};

const startEdit = link => {
  editingCode.value = link.shortCode;
  const validUntil = validUntilOf(link);
  form.value = {
    longUrl: link.longUrl || '',
    title: link.title || '',
    customSlug: link.shortCode,
    neverExpires: !validUntil,
    expiresAt: toLocalInput(validUntil),
  };
};

const submit = () => (isEditing.value ? updateLink() : createLink());

const createLink = async () => {
  if (!canSubmit.value || isSaving.value) return;
  isSaving.value = true;
  try {
    const { data } = await ShortLinksAPI.create({
      long_url: form.value.longUrl.trim(),
      title: form.value.title.trim() || undefined,
      custom_slug: form.value.customSlug.trim() || undefined,
      valid_until: expiryPayload() || undefined,
    });
    links.value = [data.payload, ...links.value];
    form.value = emptyForm();
    useAlert('Enlace acortado');
  } catch (error) {
    useAlert(error?.response?.data?.error || 'No se pudo acortar el enlace.');
  } finally {
    isSaving.value = false;
  }
};

const updateLink = async () => {
  if (!canSubmit.value || isSaving.value) return;
  isSaving.value = true;
  try {
    const { data } = await ShortLinksAPI.update(editingCode.value, {
      long_url: form.value.longUrl.trim(),
      title: form.value.title.trim(),
      valid_until: expiryPayload(),
    });
    links.value = links.value.map(l =>
      l.shortCode === editingCode.value ? data.payload : l
    );
    cancelEdit();
    useAlert('Enlace actualizado');
  } catch (error) {
    useAlert(error?.response?.data?.error || 'No se pudo actualizar el enlace.');
  } finally {
    isSaving.value = false;
  }
};

const deleteLink = async link => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`¿Eliminar el enlace ${link.shortUrl}? Dejará de funcionar.`))
    return;
  try {
    await ShortLinksAPI.delete(link.shortCode);
    links.value = links.value.filter(l => l.shortCode !== link.shortCode);
    if (editingCode.value === link.shortCode) cancelEdit();
    useAlert('Enlace eliminado');
  } catch (error) {
    useAlert(error?.response?.data?.error || 'No se pudo eliminar el enlace.');
  }
};

const copyShortUrl = async shortUrl => {
  try {
    await navigator.clipboard.writeText(shortUrl);
    useAlert('Enlace copiado');
  } catch {
    useAlert('No se pudo copiar.');
  }
};

onMounted(fetchLinks);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <div
      class="flex items-center justify-between gap-4 px-6 py-4 border-b border-n-weak flex-shrink-0"
    >
      <div class="flex flex-col">
        <h1 class="text-lg font-semibold text-n-slate-12 m-0">
          Acortador de enlaces
        </h1>
        <span class="text-sm text-n-slate-11">
          Crea enlaces cortos con un ID único usando el acortador de Flowdots.
        </span>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto p-6">
      <!-- Create / edit form -->
      <div
        class="flex flex-col gap-3 p-4 mb-6 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span v-if="isEditing" class="text-sm font-medium text-n-slate-12">
          Editar enlace
          <span class="text-[11px] font-mono px-1.5 py-0.5 ml-1 rounded-md bg-n-alpha-2 text-n-slate-10">
            {{ editingCode }}
          </span>
        </span>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">Enlace largo</span>
          <Input
            v-model="form.longUrl"
            placeholder="https://ejemplo.com/una/ruta/muy/larga"
            @keyup.enter="submit"
          />
        </label>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-11">Título (opcional)</span>
            <Input v-model="form.title" placeholder="Campaña de demo" />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-11">
              {{ isEditing ? 'Código (no editable)' : 'Slug personalizado (opcional)' }}
            </span>
            <Input
              v-model="form.customSlug"
              :disabled="isEditing"
              placeholder="mi-codigo"
            />
          </label>
        </div>
        <!-- Expiration -->
        <div class="flex flex-col gap-2">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.neverExpires"
              type="checkbox"
              class="size-4 accent-n-brand"
            />
            <span class="text-sm text-n-slate-12">Nunca expira</span>
          </label>
          <label v-if="!form.neverExpires" class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-11">Fecha de vencimiento</span>
            <input
              v-model="form.expiresAt"
              type="datetime-local"
              class="w-full h-9 px-3 rounded-lg text-sm bg-n-alpha-black2 text-n-slate-12 border border-n-weak outline-none focus:border-n-brand"
            />
            <span
              v-if="form.expiresAt && !isExpiryValid"
              class="text-xs text-n-ruby-11"
            >
              La fecha de vencimiento debe ser futura.
            </span>
          </label>
        </div>

        <div class="flex justify-end gap-2">
          <Button
            v-if="isEditing"
            label="Cancelar"
            variant="ghost"
            color="slate"
            size="sm"
            @click="cancelEdit"
          />
          <Button
            :label="isEditing ? 'Guardar cambios' : 'Acortar enlace'"
            color="blue"
            size="sm"
            icon="i-lucide-link"
            :is-loading="isSaving"
            :disabled="!canSubmit || isSaving"
            @click="submit"
          />
        </div>
      </div>

      <!-- List -->
      <div
        v-if="!links.length && !isLoading"
        class="flex flex-col items-center justify-center gap-3 py-16"
      >
        <span class="i-lucide-link-2 size-10 text-n-slate-9" />
        <span class="text-base text-n-slate-11">Aún no hay enlaces cortos.</span>
      </div>

      <div v-else class="flex flex-col gap-2">
        <div
          v-for="link in links"
          :key="link.shortCode"
          class="flex flex-col rounded-xl border border-n-weak bg-n-solid-1"
        >
          <div class="flex items-start gap-3 p-4">
          <span
            class="grid flex-shrink-0 mt-0.5 size-9 rounded-lg place-content-center bg-n-alpha-2"
          >
            <span class="i-lucide-link size-4 text-n-slate-11" />
          </span>

          <div class="flex flex-col flex-1 min-w-0 gap-1">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-[11px] font-mono px-1.5 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-10">
                {{ link.shortCode }}
              </span>
              <a
                :href="link.shortUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm font-medium text-n-brand hover:underline truncate"
              >
                {{ link.shortUrl }}
              </a>
            </div>
            <span v-if="link.title" class="text-sm text-n-slate-12">
              {{ link.title }}
            </span>
            <span class="text-xs text-n-slate-10 truncate" :title="link.longUrl">
              {{ link.longUrl }}
            </span>
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-n-slate-10">
              <span class="inline-flex items-center gap-1">
                <span class="i-lucide-eye size-3" />
                {{ visitsOf(link) }} visitas
              </span>
              <span class="inline-flex items-center gap-1">
                <span class="i-lucide-clock size-3" />
                {{ formatDate(link.dateCreated) }}
              </span>
              <span
                class="inline-flex items-center gap-1"
                :class="validUntilOf(link) ? 'text-n-amber-11' : ''"
              >
                <span class="i-lucide-hourglass size-3" />
                {{
                  validUntilOf(link)
                    ? `Vence: ${formatDate(validUntilOf(link))}`
                    : 'Sin vencimiento'
                }}
              </span>
            </div>
          </div>

          <div class="flex items-center gap-1 flex-shrink-0">
            <Button
              v-tooltip="'Ver visitas'"
              variant="ghost"
              color="slate"
              size="xs"
              :icon="
                expandedCode === link.shortCode
                  ? 'i-lucide-chevron-down'
                  : 'i-lucide-chevron-right'
              "
              @click="toggleVisits(link)"
            />
            <Button
              v-tooltip="'Actualizar'"
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-refresh-cw"
              :is-loading="refreshingCode === link.shortCode"
              @click="refreshLink(link)"
            />
            <Button
              v-tooltip="'Código QR'"
              variant="ghost"
              :color="qrCode === link.shortCode ? 'blue' : 'slate'"
              size="xs"
              icon="i-lucide-qr-code"
              @click="toggleQr(link)"
            />
            <Button
              v-tooltip="'Copiar enlace'"
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-copy"
              @click="copyShortUrl(link.shortUrl)"
            />
            <Button
              v-tooltip="'Editar'"
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-pencil"
              @click="startEdit(link)"
            />
            <Button
              v-tooltip="'Eliminar'"
              variant="ghost"
              color="ruby"
              size="xs"
              icon="i-lucide-trash-2"
              @click="deleteLink(link)"
            />
          </div>
          </div>

          <!-- QR code (generated client-side on open) -->
          <div
            v-if="qrCode === link.shortCode"
            class="flex flex-col items-center gap-3 border-t border-n-weak px-4 py-4"
          >
            <div
              v-if="qrGenerating"
              class="grid size-40 place-content-center text-xs text-n-slate-10"
            >
              Generando…
            </div>
            <img
              v-else-if="qrDataUrl"
              :src="qrDataUrl"
              :alt="`Código QR de ${link.shortUrl}`"
              class="size-40 rounded-lg border border-n-weak bg-white p-1"
            />
            <div class="flex items-center gap-2">
              <Button
                label="Copiar"
                variant="ghost"
                color="slate"
                size="xs"
                icon="i-lucide-copy"
                :disabled="!qrDataUrl"
                @click="copyQr"
              />
              <Button
                label="Descargar"
                variant="ghost"
                color="slate"
                size="xs"
                icon="i-lucide-download"
                :disabled="!qrDataUrl"
                @click="downloadQr(link)"
              />
              <Button
                label="Refrescar"
                variant="ghost"
                color="slate"
                size="xs"
                icon="i-lucide-refresh-cw"
                :is-loading="qrGenerating"
                @click="refreshQr(link)"
              />
            </div>
          </div>

          <!-- Visit detail (lazy-loaded on expand) -->
          <div
            v-if="expandedCode === link.shortCode"
            class="border-t border-n-weak px-4 py-3"
          >
            <div
              v-if="visitsByCode[link.shortCode]?.loading"
              class="py-2 text-xs text-n-slate-10"
            >
              Cargando visitas…
            </div>
            <div
              v-else-if="visitsByCode[link.shortCode]?.error"
              class="py-2 text-xs text-n-ruby-11"
            >
              {{ visitsByCode[link.shortCode].error }}
            </div>
            <div
              v-else-if="!visitsByCode[link.shortCode]?.rows.length"
              class="py-2 text-xs text-n-slate-10"
            >
              Aún no hay visitas registradas.
            </div>
            <div v-else class="overflow-x-auto">
              <table class="w-full text-xs text-left">
                <thead class="text-n-slate-10">
                  <tr>
                    <th class="py-1.5 pr-3 font-medium">Fecha</th>
                    <th class="py-1.5 pr-3 font-medium">Ubicación</th>
                    <th class="py-1.5 pr-3 font-medium">Dispositivo</th>
                    <th class="py-1.5 pr-3 font-medium">Referente</th>
                    <th class="py-1.5 font-medium">Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(visit, index) in visitsByCode[link.shortCode].rows"
                    :key="index"
                    class="border-t border-n-weak text-n-slate-11"
                  >
                    <td class="py-1.5 pr-3 whitespace-nowrap">
                      {{ formatDate(visit.date) }}
                    </td>
                    <td class="py-1.5 pr-3">
                      <span
                        v-if="locationOf(visit)"
                        class="inline-flex items-center gap-1"
                      >
                        <span class="i-lucide-map-pin size-3" />
                        {{ locationOf(visit) }}
                      </span>
                      <span v-else class="text-n-slate-9">—</span>
                    </td>
                    <td class="py-1.5 pr-3">
                      {{ parseUserAgent(visit.userAgent) }}
                    </td>
                    <td class="py-1.5 pr-3 truncate max-w-[12rem]">
                      {{ refererLabel(visit) || '—' }}
                    </td>
                    <td class="py-1.5">
                      <span
                        v-if="visitType(visit) === 'bot'"
                        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-n-amber-3 text-n-amber-11"
                      >
                        <span class="i-lucide-bot size-3" />
                        Bot
                      </span>
                      <span
                        v-else-if="visitType(visit) === 'preview'"
                        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-n-slate-3 text-n-slate-11"
                      >
                        <span class="i-lucide-eye size-3" />
                        Vista previa
                      </span>
                      <span
                        v-else
                        class="inline-flex items-center gap-1 text-n-slate-10"
                      >
                        <span class="i-lucide-user size-3" />
                        Humano
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
